import apiClient from '@/services/axios';
import ReconnectingEventSource from 'reconnecting-eventsource';
import { getEffectiveMercureUrl } from '@/services/targetOverride';

let eventSource: ReconnectingEventSource | null = null;
let eventHandler: ((data: any) => void) | null = null;

let retryCount = 0;
const MAX_RETRIES = 3;
// How often we check whether the connection ever reached OPEN. This doubles
// as the "per attempt" timeout used to count a failed reconnect.
const RETRY_CHECK_INTERVAL_MS = 5000;
let isUnsubscribed = false;
// Once true, we stop reconnecting entirely until a fresh subscribeToMercure()
// call is made (e.g. a new login/session).
let gaveUp = false;
// Whether the current subscription has reached OPEN at least once. Used to
// decide which counting mechanism owns retryCount (see comments below).
let hasEverOpened = false;
let watchdogTimer: ReturnType<typeof setInterval> | null = null;
// let renewalTimeout: ReturnType<typeof setTimeout> | null = null;

const clearWatchdog = () => {
  if (watchdogTimer) {
    clearInterval(watchdogTimer);
    watchdogTimer = null;
  }
};

const logMercure = (message: string) => {
  if (typeof window === 'undefined') return;
  (window as any).__mercureLogs__ = (window as any).__mercureLogs__ || [];
  (window as any).__mercureLogs__.push(message);
};

export const subscribeToMercure = async (
  userIdentifier: string,
  onMessage?: (data: any) => void,
  onError?: (err: unknown) => void,
  { seesAllDevices = false }: { seesAllDevices?: boolean } = {}
): Promise<() => void> => {
  if (!userIdentifier) {
    return () => {  console.log('Unsubscribed');  };
  }

  // unsubscribeFromMercure();
  if (eventSource) {
  unsubscribeFromMercure();
  }

  eventHandler = onMessage || null;
  isUnsubscribed = false;
  gaveUp = false;
  hasEverOpened = false;
  retryCount = 0;
  clearWatchdog();

  try {
    // AV4, AV8: a device has no channel of its own. Whoever sees it is reached on his own
    // channel; the Organization Admin sees every device and is reached on the admin channel.
    const res = await apiClient.post('/mercure/subscribe', {
      topics: [
        `/users/${userIdentifier}/devices`,
        `/users/${userIdentifier}/tenant-membership`,
        ...(seesAllDevices ? ['/devices/admin/all/status'] : []),
      ],
    });

    const token = res.data.token;
    const topicParams = (res.data.topics as string[])
      .map((t) => `topic=${encodeURIComponent(t)}`)
      .join('&');

    const mercureUrl = getEffectiveMercureUrl(import.meta.env.VITE_APP_MERCURE_URL).replace(/\/$/, ''); // Remove trailing slash if exists
    const url = `${mercureUrl}/.well-known/mercure?${topicParams}&authorization=${token}`;
   
    // const topic = `/users/${userIdentifier}/devices`;
    // const sseUrl = 'http://localhost:3000';

    // const url = `${sseUrl}?topic=${encodeURIComponent(topic)}`;

    // const eventSource = new EventSource(url);

    // const topic = `/users/${userIdentifier}/devices`;
    // const mercureUrl = 'http://localhost:3000';    
    // const url = `${mercureUrl}?topic=${encodeURIComponent(topic)}`;

    if (typeof window !== 'undefined') {
      (window as any).__mercureLogs__ = (window as any).__mercureLogs__ || [];
      (window as any).__mercureLogs__.push(`Mercure URL: ${mercureUrl}`);
      (window as any).__mercureLogs__.push(`Mercure full URL (token redacted): ${mercureUrl}/.well-known/mercure?${topicParams}&authorization=[REDACTED]`);
      (window as any).__mercureDebug__ = { mercureUrl, topicParams, timestamp: Date.now() };
    }

    eventSource = new ReconnectingEventSource(url, {
      withCredentials: true,
      max_retry_time: 5000,
      eventSourceClass: EventSource,
    });

    const giveUpForSession = (reason: string, err?: unknown) => {
      if (gaveUp) return;
      gaveUp = true;
      console.error(
        `Mercure: ${reason}. Reached ${MAX_RETRIES} attempts — giving up reconnecting for this session.`
      );
      logMercure(`Mercure gave up for this session after ${MAX_RETRIES} attempts: ${reason}`);
      clearWatchdog();
      unsubscribeFromMercure();
      onError?.(err instanceof Error ? err : new Error(reason));
    };

    // ReconnectingEventSource only invokes its `onerror` callback when a
    // previously OPEN connection transitions to an error state (see the
    // library source: it gates on readyState having been OPEN). When the hub
    // is unreachable from the very first attempt (e.g. local Mercure isn't
    // running), readyState never reaches OPEN, so `onerror` never fires and
    // the library retries silently forever. This watchdog only covers that
    // "never opened once" case; once we've opened at least once, `onerror`
    // fires reliably for every subsequent drop/reconnect, so we hand off
    // counting to it (see onerror below) to avoid racy double-counting.
    watchdogTimer = setInterval(() => {
      if (isUnsubscribed || gaveUp || hasEverOpened || !eventSource) return;
      if (eventSource.readyState === EventSource.OPEN) return;

      retryCount += 1;
      console.error(`Mercure connection attempt ${retryCount}/${MAX_RETRIES} not open`, {
        readyState: eventSource.readyState,
      });
      logMercure(
        `Mercure connection attempt ${retryCount}/${MAX_RETRIES} not open (readyState: ${eventSource.readyState})`
      );

      if (retryCount >= MAX_RETRIES) {
        giveUpForSession('Mercure connection timeout');
      }
    }, RETRY_CHECK_INTERVAL_MS);

    eventSource.onopen = () => {
      console.log('Mercure connected');
      if (typeof window !== 'undefined') {
        (window as any).__mercureConnected__ = true;
      }
      logMercure('Mercure connected');
      // From here on, drops/reconnects are counted deterministically via
      // onerror (reliable once the connection has been OPEN at least once),
      // so the pre-open watchdog is no longer needed for this session.
      hasEverOpened = true;
      clearWatchdog();

      
      // if (renewalTimeout) clearTimeout(renewalTimeout);
      // renewalTimeout = setTimeout(() => {
      //   console.log('Refreshing Mercure token...');
      //   subscribeToMercure(userIdentifier, onMessage, onError);
      // }, 29 * 60 * 1000); //Refresh token before expiry (29 min)
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        logMercure(`Mercure event received: ${JSON.stringify(data)}`);
        eventHandler?.(data);
      } catch (err) {
        console.error('Failed to parse Mercure event:', err);
      }
    };

    eventSource.onerror = (err: any) => {
      if (isUnsubscribed || gaveUp) return;
      const readyState = eventSource?.readyState;
      if (typeof window !== 'undefined') {
        (window as any).__mercureConnected__ = false;
      }

      // This callback only fires once the connection has been OPEN at least
      // once (library semantics), which makes it the reliable, deterministic
      // place to count drops/reconnects for this session.
      retryCount += 1;
      console.error(`Mercure error (attempt ${retryCount}/${MAX_RETRIES}):`, err, { readyState });
      logMercure(
        `Mercure onerror (attempt ${retryCount}/${MAX_RETRIES}, readyState: ${readyState}, type: ${err?.type}, status: ${err?.status})`
      );

      if (retryCount >= MAX_RETRIES) {
        giveUpForSession('Mercure connection failed');
      }
    };
  } catch (err) {
    console.error('Mercure subscribe failed:', err);
    onError?.(err);
  }

  return unsubscribeFromMercure;
};

export const unsubscribeFromMercure = () => {
  isUnsubscribed = true;
  clearWatchdog();

  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }

  // if (renewalTimeout) {
  //   clearTimeout(renewalTimeout);
  //   renewalTimeout = null;
  // }

  eventHandler = null;
  retryCount = 0;

  console.log('Unsubscribed from Mercure');
};
